import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import {
  useChannelActionContext,
  useChannelStateContext,
} from "stream-chat-react";
import { toast } from "sonner";
import { useChatFindHighlight } from "Components/Chat/ChatFindHighlightContext";
import { useDebounce } from "Hooks/useDebounce";
import {
  MESSAGE_SEARCH_MIN_LENGTH,
  useChannelMessageSearch,
} from "Hooks/Chat/useChannelMessageSearch";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.FindInConversation;

type Props = {
  onClose: () => void;
};

/**
 * "Find in conversation": replaces the channel header with a search field and a "n of N"
 * navigator. Hits are newest first; "up" steps to an older match, "down" to a newer one, Enter
 * steps older. Each step hands the message id to the SDK's jumpToMessage, which loads the
 * surrounding page if needed, scrolls to it and highlights it for its default duration.
 */
export const ChatFindInConversationBar = ({ onClose }: Props) => {
  const { t } = useResource();
  const { channel } = useChannelStateContext();
  const { jumpToMessage } = useChannelActionContext();
  const { setHighlightedMessageId } = useChatFindHighlight();

  const [term, setTerm] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isJumping, setIsJumping] = useState(false);

  const debouncedTerm = useDebounce(term.trim(), 300);
  const hasEnoughCharacters = debouncedTerm.length >= MESSAGE_SEARCH_MIN_LENGTH;
  const { data, isFetching, isError } = useChannelMessageSearch(
    channel,
    debouncedTerm,
  );
  // Below the minimum the query is disabled, but keepPreviousData still hands back the previous
  // term's hits (placeholder data ignores `enabled`). Without this gate the arrows would keep
  // stepping through, and the highlight would stay on, results for a term that is no longer there.
  const searchResult = hasEnoughCharacters ? data : undefined;
  const messageIds = searchResult?.messageIds ?? [];

  const jumpToMatch = async (messageId: string) => {
    setIsJumping(true);
    try {
      await jumpToMessage(messageId);
    } catch {
      // Loading the page around the message failed (network, or the message is gone).
      toast.error(t(RESOURCES.JumpError));
    } finally {
      setIsJumping(false);
    }
  };

  // Recreated on every render (it closes over the SDK's jumpToMessage, which is too); the effect
  // below must follow the HITS, not this identity, or it would re-jump on every render.
  const jumpToMatchRef = useRef(jumpToMatch);
  jumpToMatchRef.current = jumpToMatch;

  const jumpTo = (index: number) => {
    const messageId = messageIds[index];
    if (!messageId) {
      return;
    }
    setCurrentIndex(index);
    setHighlightedMessageId(messageId);
    void jumpToMatch(messageId);
  };

  // A new hit list (new term, the query resolving, or the term dropping below the minimum) starts
  // over at the newest match. Reacts to fetched data, not to the keystroke itself.
  useEffect(() => {
    setCurrentIndex(0);
    const newestMessageId = searchResult?.messageIds[0] ?? null;
    setHighlightedMessageId(newestMessageId);
    if (newestMessageId) {
      void jumpToMatchRef.current(newestMessageId);
    }
  }, [searchResult, setHighlightedMessageId]);

  // Closing the bar (unmount) takes the highlight with it.
  useEffect(
    () => () => setHighlightedMessageId(null),
    [setHighlightedMessageId],
  );

  const total = messageIds.length;
  const hasOlder = currentIndex < total - 1;
  const hasNewer = currentIndex > 0;

  const getStatusText = () => {
    if (!hasEnoughCharacters) {
      return null;
    }
    if (isError) {
      return t(RESOURCES.SearchError);
    }
    if (total === 0) {
      return isFetching ? null : t(RESOURCES.NoMatches);
    }
    return t(RESOURCES.Counter, {
      current: currentIndex + 1,
      total: searchResult?.hasMore ? `${total}+` : total,
    });
  };

  const statusText = getStatusText();

  return (
    <Box
      display="flex"
      alignItems="center"
      bgcolor="primary.main"
      p={2.5}
      pl={1}
      gap={1}
      flexShrink={0}
      minWidth={0}
      maxWidth="100vw"
      overflow="hidden"
    >
      <IconButton
        onClick={onClose}
        sx={{ color: "common.white" }}
        aria-label={t(RESOURCES.Close)}
      >
        <CloseIcon />
      </IconButton>
      <TextField
        size="small"
        fullWidth
        autoFocus
        placeholder={t(RESOURCES.Placeholder)}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          // Enter also commits a syllable in IMEs (Vietnamese Telex); that one must not step.
          if (e.nativeEvent.isComposing) {
            return;
          }
          if (e.key === "Enter" && hasOlder && !isJumping) {
            e.preventDefault();
            jumpTo(currentIndex + 1);
          }
        }}
        slotProps={{
          input: {
            sx: {
              borderRadius: "999px",
              bgcolor: "common.white",
              minHeight: "40px",
            },
            // Decorative only; Enter and the arrows do the navigating.
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {/* Collapses when there is nothing to say; otherwise wide enough for the longest translation
          on two lines ("Tìm kiếm không thành công"), clamped there so the arrows never move. */}
      {statusText && (
        <Typography
          variant="body2"
          color="common.white"
          maxWidth={72}
          flexShrink={0}
          textAlign="center"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {statusText}
        </Typography>
      )}
      <IconButton
        onClick={() => jumpTo(currentIndex + 1)}
        disabled={!hasOlder || isJumping}
        sx={{ color: "common.white" }}
        aria-label={t(RESOURCES.Older)}
      >
        <KeyboardArrowUpIcon />
      </IconButton>
      <IconButton
        onClick={() => jumpTo(currentIndex - 1)}
        disabled={!hasNewer || isJumping}
        sx={{ color: "common.white" }}
        aria-label={t(RESOURCES.Newer)}
      >
        <KeyboardArrowDownIcon />
      </IconButton>
    </Box>
  );
};
